import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Client } from '@/types/shelter';
import { CreditCard, Utensils, Coffee, Sunset, Droplet, Plus, History, Clock, Trash2 } from 'lucide-react';
import { useServiceLogs } from '@/hooks/useServiceLogs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ServiceRecord {
  id: string;
  type: 'transit_card' | 'laundry_card' | 'meal' | 'beverage' | 'breakfast' | 'lunch' | 'dinner' | 'milk' | 'juice';
  description: string;
  quantity?: number;
  mealType?: 'breakfast' | 'lunch' | 'dinner';
  timestamp: string;
  staffMember: string;
}

interface ClientProfileServicesProps {
  client: Client;
  userRole: string | undefined;
  userName: string | undefined;
  serviceRecords: ServiceRecord[];
  onAddService: (service: Omit<ServiceRecord, 'id' | 'timestamp' | 'staffMember'>) => void;
}

const SERVICE_OPTIONS = [
  { value: 'transit_card', label: 'Transit', icon: CreditCard, color: 'text-blue-600' },
  { value: 'laundry_card', label: 'Laundry Card', icon: CreditCard, color: 'text-purple-600' },
  { value: 'breakfast', label: 'Breakfast', icon: Coffee, color: 'text-green-600' },
  { value: 'lunch', label: 'Lunch', icon: Utensils, color: 'text-green-600' },
  { value: 'dinner', label: 'Dinner', icon: Sunset, color: 'text-green-600' },
  { value: 'milk', label: 'Milk', icon: Droplet, color: 'text-orange-600' },
  { value: 'juice', label: 'Juice', icon: Droplet, color: 'text-orange-600' },
] as const;

export const ClientProfileServices: React.FC<ClientProfileServicesProps> = ({
  client,
  userRole,
  userName,
  serviceRecords,
  onAddService
}) => {
  const [selectedService, setSelectedService] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const { deleteServiceLog } = useServiceLogs(client.id);

  const canAddServices = !!userRole;
  const canDelete = !!userRole;

  const getServiceIcon = (type: ServiceRecord['type']) => {
    switch (type) {
      case 'transit_card': return <CreditCard className="h-4 w-4" />;
      case 'laundry_card': return <CreditCard className="h-4 w-4" />;
      case 'meal': return <Utensils className="h-4 w-4" />;
      case 'breakfast': return <Coffee className="h-4 w-4" />;
      case 'lunch': return <Utensils className="h-4 w-4" />;
      case 'dinner': return <Sunset className="h-4 w-4" />;
      case 'beverage': return <Coffee className="h-4 w-4" />;
      case 'milk': return <Droplet className="h-4 w-4" />;
      case 'juice': return <Droplet className="h-4 w-4" />;
    }
  };

  const getMealIcon = (mealType?: string) => {
    switch (mealType) {
      case 'breakfast': return <Coffee className="h-4 w-4" />;
      case 'lunch': return <Utensils className="h-4 w-4" />;
      case 'dinner': return <Sunset className="h-4 w-4" />;
      default: return <Utensils className="h-4 w-4" />;
    }
  };

  const getServiceTypeColor = (type: ServiceRecord['type']) => {
    switch (type) {
      case 'transit_card': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'laundry_card': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'meal': case 'breakfast': case 'lunch': case 'dinner': return 'bg-green-100 text-green-800 border-green-200';
      case 'beverage': case 'milk': case 'juice': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleAddService = () => {
    if (!selectedService) return;

    const option = SERVICE_OPTIONS.find(o => o.value === selectedService);
    const type = selectedService as ServiceRecord['type'];
    const description = `${option?.label || selectedService} provided`;

    onAddService({
      type,
      description,
      quantity,
    });

    setSelectedService('');
    setQuantity(1);
  };

  return (
    <div className="space-y-4">
      {/* Add Service */}
      {canAddServices && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Add Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1 block">Service Type</Label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${opt.color}`} />
                            {opt.label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24">
                <Label className="text-xs text-muted-foreground mb-1 block">Qty</Label>
                <Input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleAddService} disabled={!selectedService} className="w-full sm:w-auto">
                  Add
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Service Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            Service Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {serviceRecords.length > 0 ? (
            <div className="space-y-3">
              {serviceRecords.map(record => (
                <div key={record.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${getServiceTypeColor(record.type)}`}>
                      {record.type === 'meal' ? getMealIcon(record.mealType) : getServiceIcon(record.type)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{record.description}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Clock className="h-3 w-3" />
                        <span>{new Date(record.timestamp).toLocaleDateString()} at {new Date(record.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                        <span>•</span>
                        <span>by {record.staffMember}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1">
                    <Badge variant="outline" className={getServiceTypeColor(record.type)}>
                      {record.type.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {record.quantity && (
                      <span className="text-xs text-muted-foreground">Qty: {record.quantity}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No services recorded yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
